import { Component, input, forwardRef, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule, ReactiveFormsModule, AbstractControl } from '@angular/forms';
import { ComponentSize } from '../types';

@Component({
  selector: 'app-number',
  imports: [CommonModule, FormsModule, ReactiveFormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => NumberComponent),
      multi: true
    }
  ],
  template: `
    <div class="flex flex-col gap-1.5">
      @if (label()) {
        <label 
          [for]="id() || name()"
          class="block text-sm font-semibold text-gray-700 dark:text-gray-300">
          {{ label() }}
          @if (required()) {
            <span class="text-red-500 ml-1">*</span>
          }
        </label>
      }
      
      <div class="relative">
        <input
          type="number"
          [id]="id() || name()"
          [name]="name()"
          [placeholder]="placeholder()"
          [disabled]="disabled()"
          [readonly]="readonly()"
          [min]="min()"
          [max]="max()"
          [step]="step()"
          [class]="inputClasses"
          [value]="value()"
          (input)="onInput($event)"
          (blur)="onBlur()"
          (focus)="onFocus()" />
        
        @if (showButtons()) {
          <div class="absolute right-0 top-0 bottom-0 flex flex-col border-l border-gray-300 dark:border-gray-600">
            <button
              type="button"
              [disabled]="isIncrementDisabled()"
              (click)="increment()"
              class="flex-1 px-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 15l7-7 7 7" />
              </svg>
            </button>
            <button
              type="button"
              [disabled]="isDecrementDisabled()"
              (click)="decrement()"
              class="flex-1 px-2 text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors border-t border-gray-300 dark:border-gray-600">
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
          </div>
        }
      </div>
      
      @if (helpText() && !hasError) {
        <span class="text-xs text-gray-500 dark:text-gray-400">
          {{ helpText() }}
        </span>
      }
      
      @if (hasError) {
        <span class="text-xs text-red-500 dark:text-red-400 font-medium flex items-center gap-1">
          <svg class="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20">
            <path fill-rule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
          {{ displayError }}
        </span>
      }
    </div>
  `,
  styles: []
})
export class NumberComponent implements ControlValueAccessor {
  label = input<string>('');
  placeholder = input<string>('');
  required = input<boolean>(false);
  disabled = input<boolean>(false);
  readonly = input<boolean>(false);
  min = input<number | undefined>(undefined);
  max = input<number | undefined>(undefined);
  step = input<number>(1);
  showButtons = input<boolean>(false);
  errorMessage = input<string>('');
  helpText = input<string>('');
  id = input<string>('');
  name = input<string>('');
  class = input<string>('');
  control = input<AbstractControl | null | undefined>(undefined);
  size = input<ComponentSize>('md');

  value = signal<number | null>(null);
  touched = signal(false);

  private onChange = (value: number | null) => {};
  private onTouched = () => {};

  onInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    const numValue = target.value === '' ? null : parseFloat(target.value);
    this.value.set(numValue);
    this.onChange(numValue);
    this.markAsTouched();
  }

  increment(): void {
    const current = this.value() ?? 0;
    const newValue = current + this.step();
    const maxVal = this.max();
    if (maxVal === undefined || newValue <= maxVal) {
      this.value.set(newValue);
      this.onChange(newValue);
      this.markAsTouched();
    }
  }

  decrement(): void {
    const current = this.value() ?? 0;
    const newValue = current - this.step();
    const minVal = this.min();
    if (minVal === undefined || newValue >= minVal) {
      this.value.set(newValue);
      this.onChange(newValue);
      this.markAsTouched();
    }
  }

  onBlur(): void {
    this.markAsTouched();
    this.onTouched();
  }

  onFocus(): void {
    // Focus handling
  }

  isIncrementDisabled(): boolean {
    if (this.disabled() || this.readonly()) return true;
    const maxVal = this.max();
    const currentVal = this.value();
    if (maxVal !== undefined && currentVal !== null && currentVal >= maxVal) return true;
    return false;
  }

  isDecrementDisabled(): boolean {
    if (this.disabled() || this.readonly()) return true;
    const minVal = this.min();
    const currentVal = this.value();
    if (minVal !== undefined && currentVal !== null && currentVal <= minVal) return true;
    return false;
  }

  private markAsTouched(): void {
    if (!this.touched()) {
      this.touched.set(true);
    }
  }

  writeValue(value: number | null): void {
    this.value.set(value);
  }

  registerOnChange(fn: (value: number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    // Handled by input signal
  }

  get hasError(): boolean {
    const control = this.control();
    const errorMsg = this.errorMessage();
    if (control) {
      return (control.invalid && (control.touched || this.touched())) || !!errorMsg;
    }
    return this.touched() && !!errorMsg;
  }

  get displayError(): string {
    const errorMsg = this.errorMessage();
    if (errorMsg) return errorMsg;
    const control = this.control();
    if (control && control.errors) {
      if (control.errors['required']) {
        return `${this.label() || 'This field'} is required`;
      }
      if (control.errors['min']) {
        return `Minimum value is ${control.errors['min'].min}`;
      }
      if (control.errors['max']) {
        return `Maximum value is ${control.errors['max'].max}`;
      }
    }
    return '';
  }

  get inputClasses(): string {
    const classes = [
      'w-full',
      'border',
      'rounded-lg',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'placeholder:text-gray-400',
      'dark:placeholder:text-gray-500'
    ];

    const sizeMap = {
      xs: 'px-2 py-1 text-xs',
      sm: 'px-3 py-2 text-sm',
      md: 'px-4 py-3 text-base',
      lg: 'px-5 py-4 text-lg',
      xl: 'px-6 py-5 text-xl'
    };
    classes.push(sizeMap[this.size()]);

    if (this.showButtons()) {
      classes.push('pr-10');
    }

    if (this.hasError) {
      classes.push('border-red-500', 'focus:border-red-500', 'focus:ring-2', 'focus:ring-red-100', 'dark:focus:ring-red-900/30');
    } else {
      classes.push('border-gray-300', 'dark:border-gray-600', 'focus:border-blue-600', 'dark:focus:border-blue-500', 'focus:ring-2', 'focus:ring-blue-100', 'dark:focus:ring-blue-900/30');
    }

    if (this.disabled()) {
      classes.push('opacity-60', 'cursor-not-allowed', 'bg-gray-50', 'dark:bg-gray-800');
    } else {
      classes.push('bg-white', 'dark:bg-gray-800', 'text-gray-900', 'dark:text-gray-100');
    }

    const customClass = this.class();
    if (customClass) {
      classes.push(customClass);
    }

    return classes.join(' ');
  }
}

